import { useEffect, useState } from "react";
import {
  CheckCircle,
  CloudArrowUp,
  Lock,
  ShieldCheck,
  Warning,
  Info,
} from "@phosphor-icons/react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * Iter 20.21 — Protected production maintenance card.
 *
 * Calls:
 *   GET  /api/admin/maintenance/v2-reset/status     (read-only)
 *   POST /api/admin/maintenance/v2-reset/dry-run    (no writes)
 *   POST /api/admin/maintenance/v2-reset/apply      (production-only)
 *
 * The apply button stays disabled until the admin types the exact
 * confirmation phrase. The backend independently enforces:
 *   - admin auth
 *   - the same phrase
 *   - APP_ENVIRONMENT="production"
 */
const CONFIRM_PHRASE = "CONFIRM DCG V2 RESET";

export default function V2ResetMaintenanceCard() {
  const [busy, setBusy] = useState(null);
  const [status, setStatus] = useState(null);
  const [dryRun, setDryRun] = useState(null);
  const [applied, setApplied] = useState(null);
  const [phrase, setPhrase] = useState("");

  const loadStatus = async () => {
    setBusy("status");
    try {
      const { data } = await api.get(
        "/admin/maintenance/v2-reset/status",
      );
      setStatus(data);
    } catch (e) {
      toast.error("Status fetch failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          "/admin/maintenance/v2-reset/status",
        );
        if (!cancelled) setStatus(data);
      } catch (e) {
        if (!cancelled) {
          toast.error(
            "Status fetch failed: " +
              (e.response?.data?.detail || e.message),
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const runDry = async () => {
    setBusy("dry");
    setDryRun(null);
    try {
      const { data } = await api.post(
        "/admin/maintenance/v2-reset/dry-run",
      );
      setDryRun(data);
      toast.success(
        `Dry-run OK · would tag ${data.tagging.would_tag} legacy, ` +
          `would pause ${data.operators_paused.would_pause} operators.`,
      );
    } catch (e) {
      toast.error(
        "Dry-run failed: " +
          (e.response?.data?.detail?.error ||
            JSON.stringify(e.response?.data?.detail) ||
            e.message),
      );
    } finally {
      setBusy(null);
    }
  };

  const runApply = async () => {
    if (phrase !== CONFIRM_PHRASE) {
      toast.error(`Type "${CONFIRM_PHRASE}" exactly before applying.`);
      return;
    }
    setBusy("apply");
    setApplied(null);
    try {
      const { data } = await api.post(
        "/admin/maintenance/v2-reset/apply",
        { confirmation_phrase: phrase },
      );
      setApplied(data);
      toast.success(
        `V2 reset applied · backup at ${data.backup.backup_dir}, ` +
          `tagged ${data.tagging.tagged_now}, paused ${data.operators_paused.paused_now}.`,
      );
      loadStatus();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.error === "not_production") {
        toast.error(
          `Apply blocked — current environment is "${detail.environment}". ` +
            `This action is locked to APP_ENVIRONMENT=production.`,
        );
      } else {
        toast.error(
          "Apply failed: " +
            (detail?.error || JSON.stringify(detail) || e.message),
        );
      }
    } finally {
      setBusy(null);
    }
  };

  const phraseOk = phrase === CONFIRM_PHRASE;
  const isProd = status?.environment === "production";

  return (
    <div
      className="rounded-lg border border-amber-500/40 bg-gradient-to-br from-amber-500/[0.04] to-transparent p-5 space-y-4"
      data-testid="v2-reset-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck size={18} weight="bold" className="text-amber-300" />
            <h3
              className="text-lg font-semibold text-white"
              data-testid="v2-reset-title"
            >
              Production V2 Reset / Backup
            </h3>
            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-200 border border-amber-500/30">
              {isProd ? "Production" : "Preview-safe"}
            </span>
          </div>
          <p className="text-xs text-white/60 max-w-2xl leading-relaxed">
            One-shot safety operation for the DCG V2 launch.{" "}
            <strong>Dry-run</strong> previews changes (zero writes).{" "}
            <strong>Apply</strong> exports every collection, tags untagged
            competitions as <code>{`pipeline_version="legacy"`}</code>, and pauses
            Click / Elite / LLF / BOTB / 7Days. <strong>Never publishes</strong>{" "}
            or deletes records. DCG remains launch-enabled.
          </p>
        </div>
        <button
          onClick={loadStatus}
          disabled={busy != null}
          data-testid="v2-reset-status-btn"
          className="px-3 py-1.5 rounded text-[11px] inline-flex items-center gap-1.5 bg-white/5 hover:bg-white/10 text-white/80 disabled:opacity-40"
        >
          <CheckCircle size={12} weight="bold" />
          {busy === "status" ? "Loading…" : "Confirm Production Status"}
        </button>
      </div>

      {/* Environment chip + status snapshot */}
      {status && (
        <div
          className="rounded-md bg-black/30 border border-white/10 p-3 space-y-2 text-[11px]"
          data-testid="v2-reset-status-panel"
        >
          <div className="flex items-center gap-3 flex-wrap">
            <Chip
              testId="v2-reset-env"
              label="env"
              value={status.environment}
              tone={isProd ? "warn" : "muted"}
            />
            <Chip
              testId="v2-reset-db"
              label="db"
              value={status.db_name || "?"}
              tone="muted"
            />
            <Chip
              testId="v2-reset-mongo"
              label="mongo"
              value={status.mongo_target_masked || "?"}
              tone="muted"
            />
            <Chip
              testId="v2-reset-show-demo"
              label="SHOW_DEMO"
              value={String(status.show_demo_competitions)}
              tone={status.show_demo_competitions ? "warn" : "ok"}
            />
            <Chip
              testId="v2-reset-public-count"
              label="public count (under v2 filter)"
              value={status.public_api_visibility.would_return_count}
              tone="ok"
            />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {Object.entries(status.collection_counts || {}).map(([k, v]) => (
              <Stat
                key={k}
                label={k}
                value={v}
                testId={`v2-reset-count-${k}`}
              />
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat
              label="competitions total"
              value={status.tagging.competitions_total}
              testId="v2-reset-tag-total"
            />
            <Stat
              label="legacy tagged"
              value={status.tagging.legacy_tagged}
              testId="v2-reset-tag-legacy"
              tone="ok"
            />
            <Stat
              label="v2 records"
              value={status.tagging.v2}
              testId="v2-reset-tag-v2"
              tone={status.tagging.v2 > 0 ? "ok" : "muted"}
            />
            <Stat
              label="v2 published"
              value={status.tagging.v2_published}
              testId="v2-reset-tag-v2-pub"
              tone={status.tagging.v2_published > 0 ? "ok" : "muted"}
            />
          </div>
          <OperatorStateGrid operators={status.operators} />
        </div>
      )}

      {/* Buttons */}
      <div className="flex items-center gap-3 flex-wrap pt-1">
        <button
          onClick={runDry}
          disabled={busy != null}
          data-testid="v2-reset-dry-run-btn"
          className="btn-gold px-4 py-2 rounded text-xs inline-flex items-center gap-2 disabled:opacity-50"
        >
          <Info size={14} weight="bold" />
          {busy === "dry" ? "Running dry-run…" : "Dry-run Production Reset"}
        </button>

        <div className="flex items-center gap-2 flex-1 min-w-[280px]">
          <Lock size={14} weight="bold" className="text-amber-300 shrink-0" />
          <input
            type="text"
            placeholder={`Type "${CONFIRM_PHRASE}" to enable Apply`}
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            data-testid="v2-reset-confirm-input"
            className={`px-3 py-1.5 rounded text-xs bg-black/30 border w-full font-mono ${
              phraseOk
                ? "border-emerald-500/50 text-emerald-200"
                : "border-white/15 text-white/80"
            }`}
          />
        </div>

        <button
          onClick={runApply}
          disabled={busy != null || !phraseOk}
          data-testid="v2-reset-apply-btn"
          className="px-4 py-2 rounded text-xs inline-flex items-center gap-2 bg-red-500/20 hover:bg-red-500/30 text-red-100 disabled:opacity-30 disabled:cursor-not-allowed"
          title={
            !phraseOk
              ? `Type "${CONFIRM_PHRASE}" exactly to enable Apply.`
              : isProd
                ? "Apply will mutate production DB."
                : "Backend will reject — current environment is not production."
          }
        >
          <CloudArrowUp size={14} weight="bold" />
          {busy === "apply" ? "Applying…" : "Run Backup + Tag Legacy"}
        </button>
      </div>

      {/* Dry-run report */}
      {dryRun && <ReportPanel data={dryRun} testId="v2-reset-dryrun-report" />}
      {applied && <ReportPanel data={applied} testId="v2-reset-apply-report" />}
    </div>
  );
}

function ReportPanel({ data, testId }) {
  return (
    <div
      className={`rounded-md p-3 text-[11px] space-y-2 ${
        data.mode === "apply"
          ? "bg-red-500/5 border border-red-500/30"
          : "bg-emerald-500/5 border border-emerald-500/30"
      }`}
      data-testid={testId}
    >
      <div className="font-semibold text-white/90 flex items-center gap-2">
        <Warning size={12} weight="bold" />
        {data.mode === "apply" ? "Apply report" : "Dry-run report"} ·{" "}
        actor={data.actor || "?"} · ts={data.timestamp_utc}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat
          label={data.mode === "apply" ? "tagged now" : "would tag"}
          value={
            data.mode === "apply"
              ? data.tagging.tagged_now
              : data.tagging.would_tag
          }
          testId={`${testId}-tag`}
          tone="ok"
        />
        <Stat
          label={data.mode === "apply" ? "paused now" : "would pause"}
          value={
            data.mode === "apply"
              ? data.operators_paused.paused_now
              : data.operators_paused.would_pause
          }
          testId={`${testId}-pause`}
          tone="ok"
        />
        <Stat
          label="DCG present"
          value={String(data.dcg.present)}
          testId={`${testId}-dcg`}
          tone={data.dcg.present ? "ok" : "warn"}
        />
        <Stat
          label="publishes (must be 0)"
          value={data.no_publish_assertion.delta}
          testId={`${testId}-nopublish`}
          tone={data.no_publish_assertion.ok ? "ok" : "warn"}
        />
      </div>
      {data.backup?.backup_dir && (
        <div
          className="text-[10px] font-mono text-white/55"
          data-testid={`${testId}-backup-path`}
        >
          backup_dir: {data.backup.backup_dir}
        </div>
      )}
      {data.backup?.counts && (
        <details>
          <summary className="cursor-pointer text-[10px] text-white/55">
            Backup counts ({Object.keys(data.backup.counts).length})
          </summary>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-1 pt-1">
            {Object.entries(data.backup.counts).map(([k, v]) => (
              <Stat
                key={k}
                label={k}
                value={v}
                testId={`${testId}-bk-${k}`}
                tone="muted"
              />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function OperatorStateGrid({ operators }) {
  if (!operators) return null;
  return (
    <div
      className="pt-2 border-t border-white/10"
      data-testid="v2-reset-operators-grid"
    >
      <div className="text-[10px] uppercase tracking-wide opacity-60 mb-1">
        Operator launch state
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
        {Object.entries(operators).map(([n, o]) => {
          const paused = o.launch_paused === true;
          const dcg = n === "Dream Car Giveaways";
          const tone =
            dcg && !paused
              ? "text-emerald-200"
              : paused
                ? "text-amber-200"
                : "text-white/70";
          return (
            <div
              key={n}
              className={`flex items-center justify-between px-2 py-1 rounded bg-white/5 text-[10px] ${tone}`}
              data-testid={`v2-reset-op-${n.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <span className="truncate">{n}</span>
              <span className="font-mono">
                paused={String(o.launch_paused)} ·{" "}
                pipeline={o.launch_pipeline || "-"}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Chip({ label, value, tone, testId }) {
  const toneCls =
    tone === "ok"
      ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/30"
      : tone === "warn"
        ? "bg-amber-500/15 text-amber-200 border-amber-500/30"
        : "bg-white/5 text-white/70 border-white/15";
  return (
    <span
      className={`text-[10px] font-mono px-2 py-0.5 rounded border ${toneCls}`}
      data-testid={testId}
    >
      {label}=<strong>{value}</strong>
    </span>
  );
}

function Stat({ label, value, testId, tone }) {
  const toneCls =
    tone === "ok"
      ? "bg-emerald-500/15 text-emerald-200"
      : tone === "warn"
        ? "bg-amber-500/15 text-amber-200"
        : tone === "muted"
          ? "bg-white/5 text-white/55"
          : "bg-white/5 text-white/85";
  return (
    <div
      className={`rounded px-2 py-1 ${toneCls}`}
      data-testid={testId}
    >
      <div className="text-[9px] uppercase tracking-wide opacity-70 truncate">
        {label}
      </div>
      <div className="text-xs font-semibold font-mono-num truncate">
        {value ?? 0}
      </div>
    </div>
  );
}
